import * as React from 'react';
import { DataList, useDataList, safeString } from '@components/integrated/data-list';
import type { DataListConfig } from '@components/integrated/data-list';
import { VStack } from '@components/base/layout';
import { Text } from '@components/base/display';
import { Tabs } from '@components/base/navigation';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

// ---------------------------------------------------------------------------
// Helper: Code block for displaying configuration
// ---------------------------------------------------------------------------

function ConfigCodeBlock({ config }: Readonly<{ config: DataListConfig }>) {
  return (
    <pre className="bg-[#1E293B] text-[#E2E8F0] text-xs leading-relaxed p-4 rounded-lg overflow-x-auto">
      <code>{JSON.stringify(config, null, 2)}</code>
    </pre>
  );
}

// ---------------------------------------------------------------------------
// Sample employee data
// ---------------------------------------------------------------------------

const employeeData: Record<string, unknown>[] = [
  { id: 1, name: 'Alice Martin', email: 'alice.martin@company.com', department: 'Engineering', role: 'Senior Developer', status: 'active', startDate: '2021-03-15', salary: 72000 },
  { id: 2, name: 'Bruno Lefebvre', email: 'bruno.lefebvre@company.com', department: 'Marketing', role: 'Marketing Manager', status: 'active', startDate: '2020-07-01', salary: 65000 },
  { id: 3, name: 'Claire Dupont', email: 'claire.dupont@company.com', department: 'Engineering', role: 'Tech Lead', status: 'active', startDate: '2019-01-10', salary: 85000 },
  { id: 4, name: 'David Moreau', email: 'david.moreau@company.com', department: 'Sales', role: 'Sales Representative', status: 'inactive', startDate: '2022-05-20', salary: 52000 },
  { id: 5, name: 'Emma Bernard', email: 'emma.bernard@company.com', department: 'HR', role: 'HR Specialist', status: 'active', startDate: '2021-11-08', salary: 58000 },
  { id: 6, name: 'Franck Petit', email: 'franck.petit@company.com', department: 'Engineering', role: 'Junior Developer', status: 'onLeave', startDate: '2023-02-14', salary: 45000 },
  { id: 7, name: 'Gabrielle Roux', email: 'gabrielle.roux@company.com', department: 'Marketing', role: 'Content Writer', status: 'active', startDate: '2022-09-01', salary: 48000 },
  { id: 8, name: 'Hugo Lambert', email: 'hugo.lambert@company.com', department: 'Sales', role: 'Account Executive', status: 'active', startDate: '2020-04-22', salary: 61000 },
];

// ---------------------------------------------------------------------------
// Shared DataListConfig examples
// ---------------------------------------------------------------------------

/**
 * Basic employee directory with sorting, search, and pagination.
 */
const basicConfig: DataListConfig = {
  id: 'employee-directory',
  dataSource: { data: employeeData },
  columns: {
    name: { label: 'Name', type: 'text', order: 1 },
    email: { label: 'Email', type: 'text', order: 2 },
    department: { label: 'Department', type: 'text', order: 3 },
    role: { label: 'Role', type: 'text', order: 4 },
    status: {
      label: 'Status',
      type: 'badge',
      order: 5,
      typeOptions: {
        variantMap: { active: 'success', inactive: 'warning', onLeave: 'info' },
      },
    },
    startDate: {
      label: 'Start Date',
      type: 'date',
      order: 6,
      typeOptions: { format: 'dd/MM/yyyy' },
    },
    salary: {
      label: 'Salary',
      type: 'currency',
      order: 7,
      typeOptions: { currency: 'EUR', locale: 'fr-FR' },
    },
  },
  display: {
    title: 'Employee Directory',
    titleIcon: 'users',
    hoverable: true,
    striped: true,
  },
  toolbar: {
    search: {
      enabled: true,
      placeholder: 'Search employees...',
      mode: 'client',
    },
  },
  sorting: {
    enabled: true,
    defaultSort: [{ column: 'name', direction: 'asc' }],
    mode: 'client',
  },
  pagination: {
    mode: 'pagination',
    pageSize: 5,
    pageSizeOptions: [5, 10, 25],
    showPageSize: true,
    showTotal: true,
  },
};

/**
 * Employee list with toolbar filters for department and status.
 */
const withFiltersConfig: DataListConfig = {
  id: 'filtered-list',
  dataSource: { data: employeeData },
  columns: {
    name: { label: 'Name', type: 'text', order: 1 },
    email: { label: 'Email', type: 'text', order: 2 },
    department: { label: 'Department', type: 'text', order: 3 },
    role: { label: 'Role', type: 'text', order: 4 },
    status: {
      label: 'Status',
      type: 'badge',
      order: 5,
      typeOptions: {
        variantMap: { active: 'success', inactive: 'warning', onLeave: 'info' },
      },
    },
    startDate: {
      label: 'Start Date',
      type: 'date',
      order: 6,
      typeOptions: { format: 'dd/MM/yyyy' },
    },
    salary: {
      label: 'Salary',
      type: 'currency',
      order: 7,
      typeOptions: { currency: 'EUR', locale: 'fr-FR' },
    },
  },
  display: {
    title: 'Filtered Employee List',
    titleIcon: 'filter',
  },
  toolbar: {
    search: { enabled: true, mode: 'client' },
    filters: [
      {
        id: 'department',
        label: 'Department',
        type: 'select',
        options: [
          { value: 'Engineering', label: 'Engineering' },
          { value: 'Marketing', label: 'Marketing' },
          { value: 'Sales', label: 'Sales' },
          { value: 'HR', label: 'HR' },
        ],
        placeholder: 'All departments',
      },
      {
        id: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { value: 'active', label: 'Active' },
          { value: 'inactive', label: 'Inactive' },
          { value: 'onLeave', label: 'On Leave' },
        ],
        placeholder: 'All statuses',
      },
    ],
  },
  sorting: { enabled: true, mode: 'client' },
  pagination: { mode: 'pagination', pageSize: 5 },
};

/**
 * Employee management with row actions and bulk actions.
 */
const withActionsConfig: DataListConfig = {
  id: 'actions-list',
  dataSource: {
    data: employeeData.slice(0, 6),
  },
  columns: {
    name: { label: 'Name', type: 'text', order: 1 },
    email: { label: 'Email', type: 'text', order: 2 },
    department: { label: 'Department', type: 'text', order: 3 },
    role: { label: 'Role', type: 'text', order: 4 },
    status: {
      label: 'Status',
      type: 'badge',
      order: 5,
      typeOptions: {
        variantMap: { active: 'success', inactive: 'warning', onLeave: 'info' },
      },
    },
  },
  display: {
    title: 'Employee Management',
    titleIcon: 'settings',
  },
  actions: {
    selectable: true,
    rowActions: [
      {
        id: 'edit',
        label: 'Edit',
        icon: 'pencil',
        variant: 'ghost',
        handler: 'editEmployee',
      },
      {
        id: 'delete',
        label: 'Delete',
        icon: 'trash-2',
        variant: 'danger',
        handler: 'deleteEmployee',
        confirm: {
          title: 'Delete Employee',
          message: 'Are you sure you want to delete this employee?',
          confirmLabel: 'Delete',
          variant: 'danger',
        },
      },
    ],
    bulkActions: [
      {
        id: 'export',
        label: 'Export Selected',
        icon: 'download',
        variant: 'secondary',
        handler: 'exportEmployees',
      },
      {
        id: 'archive',
        label: 'Archive Selected',
        icon: 'archive',
        variant: 'danger',
        handler: 'archiveEmployees',
        confirm: {
          title: 'Archive Employees',
          message: 'Archive all selected employees?',
          confirmLabel: 'Archive',
          variant: 'danger',
        },
      },
    ],
  },
  sorting: { enabled: true, mode: 'client' },
  pagination: { mode: 'pagination', pageSize: 5 },
};

/**
 * Full CSV export with all visible columns.
 */
const csvExportConfig: DataListConfig = {
  id: 'csv-export-full',
  dataSource: { data: employeeData },
  columns: {
    name: { label: 'Name', type: 'text', order: 1 },
    email: { label: 'Email', type: 'text', order: 2 },
    department: { label: 'Department', type: 'text', order: 3 },
    role: { label: 'Role', type: 'text', order: 4 },
    salary: {
      label: 'Salary',
      type: 'currency',
      order: 5,
      typeOptions: { currency: 'EUR', locale: 'fr-FR' },
    },
  },
  display: {
    title: 'Exportable Employee List',
    titleIcon: 'download',
  },
  toolbar: {
    search: { enabled: true, placeholder: 'Search employees...', mode: 'client' },
    export: {
      enabled: true,
      fileName: 'employees-export',
    },
  },
  sorting: { enabled: true, mode: 'client' },
  pagination: { mode: 'pagination', pageSize: 5 },
  i18n: { exportLabel: 'Export CSV' },
};

/**
 * Partial column CSV export — only name, email, and department.
 */
const csvExportPartialConfig: DataListConfig = {
  id: 'csv-export-partial',
  dataSource: { data: employeeData },
  columns: {
    name: { label: 'Name', type: 'text', order: 1 },
    email: { label: 'Email', type: 'text', order: 2 },
    department: { label: 'Department', type: 'text', order: 3 },
    role: { label: 'Role', type: 'text', order: 4 },
    salary: {
      label: 'Salary',
      type: 'currency',
      order: 5,
      typeOptions: { currency: 'EUR', locale: 'fr-FR' },
    },
  },
  display: {
    title: 'Partial Column Export',
    titleIcon: 'file-text',
  },
  toolbar: {
    search: { enabled: true, placeholder: 'Search employees...', mode: 'client' },
    export: {
      enabled: true,
      fileName: 'employees-contacts',
      columns: ['name', 'email', 'department'],
    },
  },
  sorting: { enabled: true, mode: 'client' },
  pagination: { mode: 'pagination', pageSize: 5 },
  i18n: { exportLabel: 'Export CSV' },
};

/**
 * Compact size variant with minimal columns.
 */
const compactConfig: DataListConfig = {
  id: 'compact-list',
  dataSource: {
    data: employeeData.slice(0, 5),
  },
  columns: {
    name: { label: 'Name', type: 'text', order: 1 },
    role: { label: 'Role', type: 'text', order: 2 },
    status: {
      label: 'Status',
      type: 'badge',
      order: 3,
      typeOptions: {
        variantMap: { active: 'success', inactive: 'warning', onLeave: 'info' },
      },
    },
  },
  display: {
    title: 'Quick View',
    titleIcon: 'zap',
    size: 'compact',
    collapsible: true,
  },
};

/**
 * Auto-rendered list demonstrating DataList.Auto.
 */
const autoRenderConfig: DataListConfig = {
  id: 'auto-render',
  dataSource: { data: employeeData },
  columns: {
    name: { label: 'Name', type: 'text', order: 1 },
    email: { label: 'Email', type: 'text', order: 2 },
    department: { label: 'Department', type: 'text', order: 3 },
    role: { label: 'Role', type: 'text', order: 4 },
    status: {
      label: 'Status',
      type: 'badge',
      order: 5,
      typeOptions: {
        variantMap: { active: 'success', inactive: 'warning', onLeave: 'info' },
      },
    },
  },
  display: {
    title: 'Auto-Rendered List',
    titleIcon: 'layout',
  },
  toolbar: {
    search: { enabled: true, placeholder: 'Search...', mode: 'client' },
  },
  sorting: { enabled: true, mode: 'client' },
  pagination: { mode: 'pagination', pageSize: 5 },
};

// ---------------------------------------------------------------------------
// Remote Configuration Usage Code
// ---------------------------------------------------------------------------

const remoteConfigUsageCode = `// Remote configuration mode
// The list config is fetched from the API endpoint:
// GET /api/{module}/config → { data: { config: { "employee-directory": {...} } } }

<DataList
  remoteConfigId="employee-directory"
>
  <DataList.Auto />
</DataList>

// Or with manual sub-component composition:
<DataList
  remoteConfigId="employee-directory"
>
  <DataList.Header />
  <DataList.Toolbar />
  <DataList.BulkActions />
  <DataList.Table />
  <DataList.Footer />
</DataList>

// Optional props:
// - tenant="custom-tenant"    → Override tenant from context
// - module="custom-module"    → Override module from context
// - loadingFallback={<MyLoader />} → Custom loading UI
// - errorMessage="Custom error"    → Custom error message

// Prerequisites:
// Wrap your app with IntegratedComponentProvider:
<IntegratedComponentProvider
  apiClient={axiosInstance}
  tenant="tenant-id"
  module="my-module"
>
  <App />
</IntegratedComponentProvider>`;

// ---------------------------------------------------------------------------
// Headless Mode Sub-component
// ---------------------------------------------------------------------------

const headlessConfig: DataListConfig = {
  id: 'headless-demo',
  dataSource: { data: employeeData.slice(0, 6) },
  columns: {
    name: { label: 'Name', type: 'text', order: 1 },
    department: { label: 'Department', type: 'text', order: 2 },
    status: { label: 'Status', type: 'text', order: 3 },
  },
  toolbar: {
    search: { enabled: true, mode: 'client' },
  },
  sorting: {
    enabled: true,
    defaultSort: [{ column: 'name', direction: 'asc' }],
    mode: 'client',
  },
  pagination: { mode: 'pagination', pageSize: 3 },
};

/**
 * Headless usage example -- uses the useDataList hook directly
 * without the compound component wrapper.
 */
function HeadlessListDemo() {
  const list = useDataList(headlessConfig);

  return (
    <VStack gap={4} className="max-w-2xl">
      <Text size="sm" color="muted">
        This list is built using the <code>useDataList</code> hook directly,
        without the <code>DataList</code> compound component. Full control
        over rendering while retaining all list state management.
      </Text>

      {/* Search input */}
      <div className="flex gap-3 items-center">
        <input
          type="text"
          placeholder="Search..."
          value={list.searchTerm}
          onChange={(e) => { list.setSearchTerm(e.target.value); }}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <Text size="xs" color="muted">
          {list.totalItems} result{list.totalItems === 1 ? '' : 's'}
        </Text>
      </div>

      {/* Custom table */}
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-gray-200">
            {list.visibleColumns.map((col) => (
              <th
                key={col.key}
                className="text-left py-2 px-3 font-medium text-gray-600 cursor-pointer hover:text-gray-900 select-none"
                onClick={() => { list.toggleSort(col.key); }}
              >
                {col.label}
                {list.sortState[0]?.column === col.key && (
                  <span className="ml-1">
                    {list.sortState[0].direction === 'asc' ? '\u2191' : '\u2193'}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {list.data.map((row, i) => (
            <tr key={row.id == null ? `row-${i}` : safeString(row.id)} className="border-b border-gray-100 hover:bg-gray-50">
              {list.visibleColumns.map((col) => (
                <td key={col.key} className="py-2 px-3">
                  {safeString(list.getCellValue(row, col.key))}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination controls */}
      <div className="flex gap-2 items-center justify-between text-xs">
        <div className="flex gap-1">
          <button
            type="button"
            disabled={list.page <= 1}
            onClick={() => { list.setPage(list.page - 1); }}
            className="px-2 py-1 border rounded disabled:opacity-40"
          >
            Prev
          </button>
          <button
            type="button"
            disabled={list.page >= list.totalPages}
            onClick={() => { list.setPage(list.page + 1); }}
            className="px-2 py-1 border rounded disabled:opacity-40"
          >
            Next
          </button>
        </div>
        <Text size="xs" color="muted">
          Page {list.page} of {list.totalPages}
        </Text>
      </div>

      {/* Hook state debug */}
      <div className="flex gap-4 text-xs flex-wrap">
        <Text size="xs" color="muted">
          Sort: {JSON.stringify(list.sortState)}
        </Text>
        <Text size="xs" color="muted">
          Search: {JSON.stringify(list.searchTerm)}
        </Text>
        <Text size="xs" color="muted">
          Page: {list.page}/{list.totalPages}
        </Text>
      </div>
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Main Showcase Component
// ---------------------------------------------------------------------------

function DataListShowcaseComponent({
  playgroundProps,
  isDarkMode: _isDarkMode,
}: Readonly<ShowcaseProps>) {
  const previewSize =
    (playgroundProps.size as 'default' | 'compact') ?? 'default';
  const previewStriped = playgroundProps.striped !== false;
  const previewHoverable = playgroundProps.hoverable !== false;
  const previewShowToolbar = playgroundProps.showToolbar !== false;

  /**
   * Build a memoised preview config so the list doesn't
   * re-mount on every playground prop change.
   */
  const previewConfig: DataListConfig = React.useMemo(
    () => ({
      ...basicConfig,
      display: {
        ...basicConfig.display,
        size: previewSize,
        striped: previewStriped,
        hoverable: previewHoverable,
      },
      toolbar: previewShowToolbar ? basicConfig.toolbar : { visible: false },
    }),
    [previewSize, previewStriped, previewHoverable, previewShowToolbar],
  );

  return (
    <VStack gap={6}>
      {/* ---- Interactive Preview ---- */}
      <ShowcasePreviewSection componentName="data list">
        <div className="w-full">
          <DataList config={previewConfig}>
            <DataList.Header />
            {previewShowToolbar && <DataList.Toolbar />}
            <DataList.BulkActions />
            <DataList.Table />
            <DataList.Footer />
          </DataList>
        </div>
      </ShowcasePreviewSection>

      {/* ---- Basic Data Table ---- */}
      <ShowcaseDemoSection
        title="Basic Data Table"
        description="A sortable, paginated employee directory with search, badge statuses, currency formatting, and date formatting"
       
      >
        <Tabs defaultValue="preview" variant="contained">
          <Tabs.List>
            <Tabs.Tab value="preview">Preview</Tabs.Tab>
            <Tabs.Tab value="config">Configuration</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="preview">
            <div className="w-full">
              <DataList config={basicConfig}>
                <DataList.Header />
                <DataList.Toolbar />
                <DataList.Table />
                <DataList.Footer />
              </DataList>
            </div>
          </Tabs.Panel>
          <Tabs.Panel value="config">
            <ConfigCodeBlock config={basicConfig} />
          </Tabs.Panel>
        </Tabs>
      </ShowcaseDemoSection>

      {/* ---- Auto Rendering ---- */}
      <ShowcaseDemoSection
        title="Auto Rendering"
        description="DataList.Auto renders all sub-components (Header, Toolbar, Table, Footer) automatically based on the config"
       
      >
        <Tabs defaultValue="preview" variant="contained">
          <Tabs.List>
            <Tabs.Tab value="preview">Preview</Tabs.Tab>
            <Tabs.Tab value="config">Configuration</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="preview">
            <div className="w-full">
              <DataList config={autoRenderConfig}>
                <DataList.Auto />
              </DataList>
            </div>
          </Tabs.Panel>
          <Tabs.Panel value="config">
            <ConfigCodeBlock config={autoRenderConfig} />
          </Tabs.Panel>
        </Tabs>
      </ShowcaseDemoSection>

      {/* ---- With Filters ---- */}
      <ShowcaseDemoSection
        title="With Filters"
        description="Toolbar filters allow narrowing results by department or status using dropdown selectors"
       
      >
        <Tabs defaultValue="preview" variant="contained">
          <Tabs.List>
            <Tabs.Tab value="preview">Preview</Tabs.Tab>
            <Tabs.Tab value="config">Configuration</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="preview">
            <div className="w-full">
              <DataList config={withFiltersConfig}>
                <DataList.Header />
                <DataList.Toolbar />
                <DataList.Table />
                <DataList.Footer />
              </DataList>
            </div>
          </Tabs.Panel>
          <Tabs.Panel value="config">
            <ConfigCodeBlock config={withFiltersConfig} />
          </Tabs.Panel>
        </Tabs>
      </ShowcaseDemoSection>

      {/* ---- Row & Bulk Actions ---- */}
      <ShowcaseDemoSection
        title="Row & Bulk Actions"
        description="Row-level edit/delete actions with confirmation dialogs, plus bulk export and archive operations on selected rows"
       
      >
        <Tabs defaultValue="preview" variant="contained">
          <Tabs.List>
            <Tabs.Tab value="preview">Preview</Tabs.Tab>
            <Tabs.Tab value="config">Configuration</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="preview">
            <div className="w-full">
              <DataList config={withActionsConfig}>
                <DataList.Header />
                <DataList.Toolbar />
                <DataList.BulkActions />
                <DataList.Table />
                <DataList.Footer />
              </DataList>
            </div>
          </Tabs.Panel>
          <Tabs.Panel value="config">
            <ConfigCodeBlock config={withActionsConfig} />
          </Tabs.Panel>
        </Tabs>
      </ShowcaseDemoSection>

      {/* ---- Export to CSV ---- */}
      <ShowcaseDemoSection
        title="Export to CSV"
        description="Built-in CSV export adds a download button to the toolbar. Configure file name, column selection, and header inclusion via the toolbar.export config"
       
      >
        <Tabs defaultValue="full" variant="contained">
          <Tabs.List>
            <Tabs.Tab value="full">Full Export</Tabs.Tab>
            <Tabs.Tab value="partial">Partial Columns</Tabs.Tab>
            <Tabs.Tab value="config">Configuration</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="full">
            <div className="w-full">
              <DataList config={csvExportConfig}>
                <DataList.Header />
                <DataList.Toolbar />
                <DataList.Table />
                <DataList.Footer />
              </DataList>
            </div>
          </Tabs.Panel>
          <Tabs.Panel value="partial">
            <div className="w-full">
              <DataList config={csvExportPartialConfig}>
                <DataList.Header />
                <DataList.Toolbar />
                <DataList.Table />
                <DataList.Footer />
              </DataList>
            </div>
          </Tabs.Panel>
          <Tabs.Panel value="config">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <Text size="sm" weight="medium" className="mb-2">Full Export</Text>
                <ConfigCodeBlock config={csvExportConfig} />
              </div>
              <div>
                <Text size="sm" weight="medium" className="mb-2">Partial Columns</Text>
                <ConfigCodeBlock config={csvExportPartialConfig} />
              </div>
            </div>
          </Tabs.Panel>
        </Tabs>
      </ShowcaseDemoSection>

      {/* ---- Compact & Collapsible ---- */}
      <ShowcaseDemoSection
        title="Compact & Collapsible"
        description="A compact table with fewer columns and a collapsible header for space-constrained layouts"
       
      >
        <Tabs defaultValue="preview" variant="contained">
          <Tabs.List>
            <Tabs.Tab value="preview">Preview</Tabs.Tab>
            <Tabs.Tab value="config">Configuration</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="preview">
            <div className="w-full">
              <DataList config={compactConfig}>
                <DataList.Header />
                <DataList.Table />
              </DataList>
            </div>
          </Tabs.Panel>
          <Tabs.Panel value="config">
            <ConfigCodeBlock config={compactConfig} />
          </Tabs.Panel>
        </Tabs>
      </ShowcaseDemoSection>

      {/* ---- Headless Mode ---- */}
      <ShowcaseDemoSection
        title="Headless Mode"
        description="Using the useDataList hook directly for full rendering control without the compound component"
       
      >
        <Tabs defaultValue="preview" variant="contained">
          <Tabs.List>
            <Tabs.Tab value="preview">Preview</Tabs.Tab>
            <Tabs.Tab value="config">Configuration</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="preview">
            <HeadlessListDemo />
          </Tabs.Panel>
          <Tabs.Panel value="config">
            <ConfigCodeBlock config={headlessConfig} />
          </Tabs.Panel>
        </Tabs>
      </ShowcaseDemoSection>

      {/* ---- Remote Configuration ---- */}
      <ShowcaseDemoSection
        title="Remote Configuration"
        description="This list loads its configuration from the remote API endpoint (GET /api/{module}/config) using the remoteConfigId prop. The IntegratedComponentProvider in the component-explorer routes supplies the API client, tenant and module."
       
      >
        <pre className="bg-[#1E293B] text-[#E2E8F0] text-xs leading-relaxed p-4 rounded-lg overflow-x-auto">
          <code>{remoteConfigUsageCode}</code>
        </pre>
      </ShowcaseDemoSection>
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Showcase Definition
// ---------------------------------------------------------------------------

export const DataListShowcase: ShowcaseDefinition = {
  id: 'data-list',
  name: 'DataList',
  category: 'data-lists',
  description:
    'Configuration-driven data list component with sorting, filtering, pagination, row actions, bulk actions, and headless mode',
  propsDefinition: [
    {
      name: 'size',
      type: 'select',
      options: ['default', 'compact'],
      defaultValue: 'default',
      description: 'Table density size variant',
    },
    {
      name: 'striped',
      type: 'boolean',
      defaultValue: true,
      description: 'Show alternating row colors',
    },
    {
      name: 'hoverable',
      type: 'boolean',
      defaultValue: true,
      description: 'Highlight rows on hover',
    },
    {
      name: 'showToolbar',
      type: 'boolean',
      defaultValue: true,
      description: 'Show the toolbar with search',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'role="table" on the data table element',
      'role="row" on each table row',
      'role="columnheader" on header cells with aria-sort',
      'aria-selected on selectable rows',
      'aria-checked on selection checkboxes',
      'aria-label on action buttons',
      'aria-live="polite" on status/count updates',
    ],
    keyboardNav: [
      'Tab: Navigate between interactive elements (search, filters, checkboxes, action buttons)',
      'Enter/Space: Toggle sort on column headers',
      'Enter/Space: Toggle row selection checkboxes',
      'Enter/Space: Activate action buttons',
    ],
    notes: [
      'Table headers indicate sort direction via aria-sort attribute',
      'Empty state is announced to screen readers',
      'Bulk action bar appears/disappears with appropriate focus management',
      'Pagination controls use semantic nav element',
    ],
  },
  component: DataListShowcaseComponent,
};
